import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LockIcon, LogOutIcon, UsersIcon, TrashIcon, MailIcon, PhoneIcon, MapPinIcon, CalendarIcon, SearchIcon, DownloadIcon, ChevronDownIcon, ChevronUpIcon, ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
export interface NetworkSubmission {
  id: string;
  timestamp: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  reasons: string[];
  socialInstagram?: string;
  socialFacebook?: string;
  socialTiktok?: string;
  nonprofitName?: string;
  nonprofitDirector?: string;
  nonprofitWebsite?: string;
  faithCity?: string;
  faithState?: string;
  faithOrgName?: string;
  faithWebsite?: string;
  businessName?: string;
  businessContact?: string;
  businessPhone?: string;
  businessWebsite?: string;
  accomplishmentWebsite?: string;
  accomplishmentSocial?: string;
  accomplishmentLinkedin?: string;
  identifiesHonduran?: string;
}
const STORAGE_KEY = 'haa_network_submissions';
const ADMIN_USER = 'jaymiranda';
const ADMIN_PASS = 'Tahoe@911$';
export function getSubmissions(): NetworkSubmission[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}
export function saveSubmission(sub: NetworkSubmission) {
  const existing = getSubmissions();
  existing.unshift(sub);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
}
function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
function SubmissionCard({
  sub,
  onDelete
}: {
  sub: NetworkSubmission;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return <motion.div layout initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-97" data-mp="2au0g91fbmlon">
      <div className="p-5 cursor-pointer hover:bg-gray-50/50 transition-colors" onClick={() => setExpanded(!expanded)} data-id="element-98" data-mp="259azbwbqfknb">
        <div className="flex items-start justify-between gap-3" data-id="element-99" data-mp="1w71qrc00y1ck">
          <div className="flex-1 min-w-0" data-id="element-100" data-mp="1w7bhx2wt2eju">
            <div className="flex items-center gap-2 flex-wrap" data-id="element-101" data-mp="2jww37sgflnwa">
              <h3 className="font-nunito font-bold text-haa-navy text-base" data-id="element-102" data-mp="2jyqnd4jvpbzb">
                {sub.firstName} {sub.lastName}
              </h3>
              <span className="text-xs font-nunito text-gray-400 flex items-center gap-1" data-id="element-103" data-mp="2a7cisid2llzb">
                <CalendarIcon className="w-3 h-3" data-id="element-104" data-mp="2f5dzwxqxrb85" />
                {formatDate(sub.timestamp)}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1.5 flex-wrap" data-id="element-105" data-mp="32uq7xuaj1d8v">
              <span className="text-sm font-nunito text-gray-500 flex items-center gap-1" data-id="element-106" data-mp="37mqew6p0l6ey">
                <MailIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-107" data-mp="3h0ie6vepfjlx" />
                {sub.email}
              </span>
              {sub.phone && <span className="text-sm font-nunito text-gray-500 flex items-center gap-1" data-id="element-108" data-mp="3cyx41htk1jff">
                  <PhoneIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-109" data-mp="3qqbq5xire0qp" />
                  {sub.phone}
                </span>}
              {(sub.city || sub.state) && <span className="text-sm font-nunito text-gray-500 flex items-center gap-1" data-id="element-110" data-mp="2y37dr7hc6k4w">
                  <MapPinIcon className="w-3.5 h-3.5 text-haa-blue" data-id="element-111" data-mp="2u1m7i4zb543o" />
                  {[sub.city, sub.state].filter(Boolean).join(', ')}
                </span>}
            </div>
            {sub.reasons.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-112" data-mp="32zxuzy4vnlrw">
                {sub.reasons.map((r, i) => <span key={i} className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-haa-blue font-nunito text-[11px] font-semibold" data-id="element-113" data-mp="nc7l2zfkb4y9" data-mpi={i}>
                    {r}
                  </span>)}
              </div>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-114" data-mp="2xxhwplkifpng">
            <button onClick={e => {
            e.stopPropagation();
            onDelete(sub.id);
          }} className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors" title="Delete" data-id="element-115" data-mp="2xw6u3p2swnxx">
              <TrashIcon className="w-3.5 h-3.5 text-red-500" data-id="element-116" data-mp="3bv6h6sao0urz" />
            </button>
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center" data-id="element-117" data-mp="3gohpyyz1flmj">
              {expanded ? <ChevronUpIcon className="w-4 h-4 text-gray-400" data-id="element-118" data-mp="3lovp1rinvl8f" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-119" data-mp="3pqh5tnybgjnw" />}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-120" data-mp="1j0x3dhm44bqo">
        {expanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-121" data-mp="1j1ozqunjadi0">
            <div className="px-5 pb-5 pt-0 border-t border-gray-100" data-id="element-122" data-mp="21szrtf4zwwbh">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4" data-id="element-123" data-mp="1xptlk7hvv550">
                {sub.nonprofitName && <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100" data-id="element-124" data-mp="1swqmjjuc73hf">
                    <p className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-125" data-mp="1nyyvst5fhnet">
                      Nonprofit Details
                    </p>
                    <p className="font-nunito text-sm text-haa-navy" data-id="element-126" data-mp="2gn4gxh0e9ef6">
                      <strong data-id="element-127" data-mp="2zjwbiyzmkuj4">Name:</strong> {sub.nonprofitName}
                    </p>
                    {sub.nonprofitDirector && <p className="font-nunito text-sm text-haa-navy" data-id="element-128" data-mp="34i6alkoh0fa0">
                        <strong data-id="element-129" data-mp="2qsw2wguskfd9">Director:</strong> {sub.nonprofitDirector}
                      </p>}
                    {sub.nonprofitWebsite && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-130" data-mp="2gtwpym2ztxkg">
                        <strong data-id="element-131" data-mp="2gvzro3didf50">Website:</strong> {sub.nonprofitWebsite}
                      </p>}
                  </div>}
                {sub.businessName && <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100" data-id="element-132" data-mp="27vbefavxvit6">
                    <p className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-133" data-mp="2bv3ndop8vqz2">
                      Business Details
                    </p>
                    <p className="font-nunito text-sm text-haa-navy" data-id="element-134" data-mp="a25rfk068i9w">
                      <strong data-id="element-135" data-mp="1ukzs4s7emup3">Name:</strong> {sub.businessName}
                    </p>
                    {sub.businessWebsite && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-136" data-mp="1qlh77wfl2jm9">
                        <strong data-id="element-137" data-mp="24cchnd5qd8ry">Website/Social:</strong> {sub.businessWebsite}
                      </p>}
                  </div>}
                {(sub.accomplishmentWebsite || sub.accomplishmentSocial || sub.accomplishmentLinkedin) && <div className="bg-blue-50/50 rounded-xl p-3 border border-blue-100" data-id="element-138" data-mp="299m5wphdvee7">
                    <p className="font-nunito text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1" data-id="element-139" data-mp="2n93zrj9jdhuf">
                      Accomplishments
                    </p>
                    {sub.accomplishmentWebsite && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-140" data-mp="swe4ax3is0kx">
                        <strong data-id="element-141" data-mp="nym1v9590w2h">Website:</strong> {sub.accomplishmentWebsite}
                      </p>}
                    {sub.accomplishmentSocial && <p className="font-nunito text-sm text-haa-navy" data-id="element-142" data-mp="726vzhiuvrmc">
                        <strong data-id="element-143" data-mp="btymjs20hxch">Social:</strong> {sub.accomplishmentSocial}
                      </p>}
                    {sub.accomplishmentLinkedin && <p className="font-nunito text-sm text-haa-blue break-all" data-id="element-144" data-mp="guw545qxotei">
                        <strong data-id="element-145" data-mp="3g9pzfs71s5bm">LinkedIn:</strong> {sub.accomplishmentLinkedin}
                      </p>}
                  </div>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function AdminDashboard({
  onBack
}: {
  onBack: () => void;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [submissions, setSubmissions] = useState<NetworkSubmission[]>([]);
  const [search, setSearch] = useState('');
  useEffect(() => {
    if (isAuthenticated) {
      setSubmissions(getSubmissions());
    }
  }, [isAuthenticated]);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (username === ADMIN_USER && password === ADMIN_PASS) {
      setIsAuthenticated(true);
      setLoginError('');
    } else {
      setLoginError('Invalid username or password');
    }
  };
  const handleDelete = (id: string) => {
    const updated = submissions.filter(s => s.id !== id);
    setSubmissions(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };
  const handleExportCSV = () => {
    if (submissions.length === 0) return;
    const headers = ['Date', 'First Name', 'Last Name', 'Email', 'Phone', 'City', 'State', 'Reasons', 'Nonprofit Name', 'Nonprofit Director', 'Nonprofit Website', 'Business Name', 'Business Website', 'Accomplishment Website', 'Accomplishment Social', 'Accomplishment LinkedIn'];
    const rows = submissions.map(s => [formatDate(s.timestamp), s.firstName, s.lastName, s.email, s.phone, s.city, s.state, s.reasons.join('; '), s.nonprofitName || '', s.nonprofitDirector || '', s.nonprofitWebsite || '', s.businessName || '', s.businessWebsite || '', s.accomplishmentWebsite || '', s.accomplishmentSocial || '', s.accomplishmentLinkedin || '']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `haa-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const filtered = submissions.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.firstName.toLowerCase().includes(q) || s.lastName.toLowerCase().includes(q) || s.email.toLowerCase().includes(q) || s.city.toLowerCase().includes(q) || s.state.toLowerCase().includes(q) || s.reasons.some(r => r.toLowerCase().includes(q));
  });
  // Login Screen
  if (!isAuthenticated) {
    return <div className="min-h-screen bg-gradient-to-br from-haa-navy via-haa-blue to-haa-midblue flex items-center justify-center p-4" data-id="element-146" data-mp="12txqxxj6w9t8">
        <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8" data-id="element-147" data-mp="16uhrzc6vvxkn">
          <div className="text-center mb-8" data-id="element-148" data-mp="1t59bwnphcylo">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center mx-auto mb-4 shadow-lg" data-id="element-149" data-mp="1t6ar94dj4797">
              <LockIcon className="w-6 h-6 text-white" data-id="element-150" data-mp="1jddcn9bnr2fv" />
            </div>
            <h1 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-151" data-mp="1fd2y389sv3pe">
              Admin Access
            </h1>
            <p className="font-nunito text-gray-500 text-sm mt-1" data-id="element-152" data-mp="1acnkqgpipw7f">
              HAA Network Submissions
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" data-id="element-153" data-mp="15ggfkq4y4oqj">
            <div data-id="element-154" data-mp="10gkjdd2hq1lj">
              <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-155" data-mp="10h3rk22c9bmd">
                Username
              </label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors" placeholder="Enter username" autoComplete="username" data-id="element-156" data-mp="2ut21k68h2bjb" />
            </div>
            <div data-id="element-157" data-mp="3h3tmnl2t1ilx">
              <label className="font-nunito text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block" data-id="element-158" data-mp="2kbg9or633vj4">
                Password
              </label>
              <div className="relative" data-id="element-159" data-mp="2odjy0gduqult">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 pr-12 rounded-xl border-2 border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors" placeholder="Enter password" autoComplete="current-password" data-id="element-160" data-mp="2tc4pnrf1g5yn" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-161" data-mp="1mjs10fcd4xu6">
                  {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-162" data-mp="1qlvetami8eim" /> : <EyeIcon className="w-4 h-4" data-id="element-163" data-mp="25eypcy1ohrzu" />}
                </button>
              </div>
            </div>

            <AnimatePresence data-id="element-164" data-mp="2e93hrlwgnbhh">
              {loginError && <motion.p initial={{
              opacity: 0,
              y: -5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} className="font-nunito text-red-500 text-sm text-center" data-id="element-165" data-mp="2e8bnqfiihltb">
                  {loginError}
                </motion.p>}
            </AnimatePresence>

            <motion.button type="submit" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full bg-gradient-to-r from-haa-blue to-haa-midblue text-white font-nunito font-bold text-base py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all" data-id="element-166" data-mp="37qqcqph5qu96">
              Sign In
            </motion.button>
          </form>

          <button onClick={onBack} className="w-full mt-4 flex items-center justify-center gap-2 font-nunito text-sm text-gray-400 hover:text-haa-blue transition-colors" data-id="element-167" data-mp="2a6nctzqku4y5">
            <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-168" data-mp="2155vyf5chz8e" />
            Back to site
          </button>
        </motion.div>
      </div>;
  }
  // Dashboard
  return <div className="min-h-screen bg-gray-50" data-id="element-169" data-mp="bo0iancisw6r">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-170" data-mp="bpblom2201xc">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between" data-id="element-171" data-mp="6vh902kv8n7r">
          <div className="flex items-center gap-3" data-id="element-172" data-mp="1xyuzxrzrhsw">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow" data-id="element-173" data-mp="3t4ie007zee2a">
              <UsersIcon className="w-4 h-4 text-white" data-id="element-174" data-mp="uluqx09ql5om" />
            </div>
            <div data-id="element-175" data-mp="pn06jtw4f92e">
              <h1 className="font-bebas text-xl text-haa-navy tracking-wider leading-tight" data-id="element-176" data-mp="pol1hkmcku0s">
                Network Submissions
              </h1>
              <p className="font-nunito text-xs text-gray-400" data-id="element-177" data-mp="gmljoq3idlws">
                {submissions.length} total submissions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-178" data-mp="35fqs1qkzirxy">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={onBack} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 font-nunito text-xs font-semibold text-gray-600 transition-colors" data-id="element-179" data-mp="31ckr8ygn5syv">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-180" data-mp="1umq4hntjl2p4" />
              Site
            </motion.button>
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => {
            setIsAuthenticated(false);
            setUsername('');
            setPassword('');
          }} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 font-nunito text-xs font-semibold text-red-600 transition-colors" data-id="element-181" data-mp="1zlsymd7zhoib">
              <LogOutIcon className="w-3.5 h-3.5" data-id="element-182" data-mp="j6879li4ew1f" />
              Logout
            </motion.button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-id="element-183" data-mp="2l2qutju4b4f2">
        {/* Search + Export */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6" data-id="element-184" data-mp="2h2gmw1mrm3xk">
          <div className="relative flex-1" data-id="element-185" data-mp="2utv88f45ah7s">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-186" data-mp="2pva5a7xidjfn" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, city, reason..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 font-nunito text-sm text-haa-navy focus:border-haa-blue focus:ring-0 outline-none transition-colors bg-white" data-id="element-187" data-mp="225fvy0ceed05" />
          </div>
          <motion.button whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} onClick={handleExportCSV} disabled={submissions.length === 0} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-nunito text-sm font-bold transition-all ${submissions.length > 0 ? 'bg-haa-navy text-white hover:bg-haa-blue shadow' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-188" data-mp="3ni968uv8hdjc">
            <DownloadIcon className="w-4 h-4" data-id="element-189" data-mp="109tf6obdiu60" />
            Export CSV
          </motion.button>
        </div>

        {/* Submissions List */}
        {filtered.length === 0 ? <div className="text-center py-20" data-id="element-190" data-mp="1s1ix8lgqvg9b">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4" data-id="element-191" data-mp="1s19ad6qzxp7u">
              <UsersIcon className="w-7 h-7 text-gray-300" data-id="element-192" data-mp="1ea46ttkiccs8" />
            </div>
            <p className="font-nunito text-gray-400 text-base" data-id="element-193" data-mp="1j85vqv4m76bm">
              {submissions.length === 0 ? 'No submissions yet. They will appear here when users submit the Join Our Network form.' : 'No results match your search.'}
            </p>
          </div> : <div className="space-y-3" data-id="element-194" data-mp="2rzsldi4x18ge">
            {filtered.map(sub => <SubmissionCard key={sub.id} sub={sub} onDelete={handleDelete} data-id="element-195" data-mp="2wxamimcoyjdu" />)}
          </div>}
      </div>
    </div>;
}